import Button from 'tio-ui/components/button';
import { concat } from '@ember/helper';
import { not, or } from 'tio-ui/utilities';
import { Section, VStack } from 'tio-ui/components/layout';
import PageHeader from 'tio-ui/components/page-header';
import { registerDestructor } from '@ember/destroyable';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import EligibilityFilesDownloadEligibilityFile from 'tio-common/components/eligibility-files/download-eligibility-file';
import DetailsBox from 'tio-common/components/eligibility-files/details-box';
import RecordDetailsModal from 'tio-common/components/eligibility-files/record-details-modal';
import TopChangesModal from 'tio-common/components/eligibility-files/top-changes-modal';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatTime from 'tio-common/helpers/safe-format-time';
import Table from 'tio-ui/components/table';
import TioLoadingSpinner from 'tio-common/components/tio/loading-spinner';
import type { EligibilityFileJobLogExtension as EligibilityFileJobLogModel } from 'tio-data/data/resources/eligibility-file-job-log.ext';
import type { PreviewCounts } from 'tio-data/data/resources/eligibility-file-job-log.type';
import type EligibilityUploadService from 'tio-common/services/eligibility-upload';
import type IntlService from 'ember-intl/services/intl';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import { reloadRecord } from 'tio-data/requests/save-record';
import type { Store as StoreService } from '@warp-drive/core';
import { resolveAuditUserName } from 'tio-common/utils/resolve-audit-user';
import {
  processEligibilityFileRequest,
  abortEligibilityFileRequest,
  startPollingForEligibilityStatus,
  stopPollingForEligibilityStatus,
} from 'tio-data/requests/eligibility-file-actions';
import { PhInfo } from 'ember-phosphor-icons';

interface AuthenticatedAdminEligibilityFilesShowRouteSignature {
  Args: {
    model: EligibilityFileJobLogModel;
  };
}

export default class AuthenticatedAdminEligibilityFilesShowRouteComponent extends Component<AuthenticatedAdminEligibilityFilesShowRouteSignature> {
  @service declare store: StoreService;
  @service declare router: RouterService;
  @service declare eligibilityUpload: EligibilityUploadService;
  @service declare intl: IntlService;

  @tracked isProcessing = false;
  @tracked isAborting = false;
  @tracked canProcess = true;
  @tracked canAbort = true;
  @tracked buttonEnabled = true;
  @tracked activeCategory = '';
  @tracked isRecordModalOpen = false;
  @tracked isTopChangesOpen = false;

  openViewDetails = (category: string) => {
    this.activeCategory = category;
    this.isRecordModalOpen = true;
  };

  closeRecordModal = () => {
    this.isRecordModalOpen = false;
    this.activeCategory = '';
  };

  openTopChanges = () => {
    this.isTopChangesOpen = true;
  };

  closeTopChanges = () => {
    this.isTopChangesOpen = false;
  };

  /** The translated modal heading for the open category, or '' when none is open. */
  get activeCategoryTitle() {
    if (!this.activeCategory) {
      return '';
    }

    const titleKeys: Record<string, string> = {
      created: 'eligibility_files.new_employees',
      updated: 'eligibility_files.changed_employees',
      deactivated: 'eligibility_files.deactivated_employees',
      reactivated: 'eligibility_files.reactivated_employees',
    };
    const key = titleKeys[this.activeCategory];

    return key ? this.intl.t(key) : this.activeCategory;
  }

  // A job log whose preview stage has not run serves `previewCounts: null`, so a
  // category with nothing in it offers no drill-down rather than an empty modal.
  private hasPreviewed(category: keyof PreviewCounts) {
    return (this.args.model.previewCounts?.[category] ?? 0) > 0;
  }

  get canViewCreated() {
    return this.hasPreviewed('created');
  }

  get canViewUpdated() {
    return this.hasPreviewed('updated');
  }

  get canViewDeactivated() {
    return this.hasPreviewed('deactivated');
  }

  get canViewReactivated() {
    return this.hasPreviewed('reactivated');
  }

  constructor(owner: Owner, args: AuthenticatedAdminEligibilityFilesShowRouteSignature['Args']) {
    super(owner, args);

    registerDestructor(this, () => {
      if (this.args.model) {
        stopPollingForEligibilityStatus(this.args.model);
      }
    });
    this.beginPolling();
  }

  get processEnabled() {
    return (
      this.args.model.canProcess &&
      this.canProcess === true &&
      this.args.model.status !== 'REQUIRES_REVIEWER_APPROVAL' &&
      this.buttonEnabled === true
    );
  }

  get abortEnabled() {
    return (this.args.model.canAbort && this.canAbort) === true && this.buttonEnabled === true;
  }

  async beginPolling() {
    await this.eligibilityUpload.uploadEligibilityFile();
    startPollingForEligibilityStatus(this.store, this.args.model);
  }

  // `createdBy` is a PaperTrail whodunnit — resolve to a name only when it's a
  // real user id, never issue GET /users/<non-id> (404 → unhandled rejection).
  uploadedByData = trackedFunction(this, () =>
    resolveAuditUserName(this.store, this.args.model.createdBy)
  );

  get uploaderName() {
    return this.uploadedByData.value || '';
  }

  processEligibilityFile = async () => {
    const { legacyId, id } = this.args.model.company;
    const { processId } = this.args.model;
    this.buttonEnabled = false;
    this.canProcess = false;
    this.isProcessing = true;
    await this.store.request(processEligibilityFileRequest(legacyId, id!, processId));
    // KEPT (default-safe): the process endpoint renders a full
    // `eligibility-file-job-log` resource, but the builder's request identifier
    // type is `eligibility-files` (the resourcePath), NOT the rendered
    // `eligibility-file-job-log` type — so it is NOT verifiable that the
    // CacheHandler merges the response into THIS record. Preserve the refetch.
    await reloadRecord(this.store, this.args.model);
    this.isProcessing = false;
    this.buttonEnabled = true;
    this.canProcess = true;
  };

  abortEligibilityFile = async () => {
    const { legacyId, id } = this.args.model.company;
    const { processId } = this.args.model;
    this.buttonEnabled = false;
    this.canAbort = false;
    this.isAborting = true;
    await this.store.request(abortEligibilityFileRequest(legacyId, id!, processId));
    // abort_eligibility_file does `render status: 204` (NO resource body), so
    // the builder does NOT refresh the cache — keep the reload via the helper.
    await reloadRecord(this.store, this.args.model);
    this.isAborting = false;
    this.buttonEnabled = true;
    this.canAbort = true;
  };

  <template>
    <VStack>
      <PageHeader @title={{t "eligibility_files.default"}} @sticky={{false}}>
        <:breadcrumbs as |b|>
          <b.crumb
            @route="authenticated.admin.eligibility-files.index"
            @label="Eligibility Files"
          />
          <b.crumb @label={{@model.fileName}} />
        </:breadcrumbs>
      </PageHeader>
      <div class="grid grid-cols-1 lg:grid-cols-5 mt-8 gap-4">
        <DetailsBox
          @title={{t "eligibility_files.unchanged_employees"}}
          @category="unchanged"
          @preview={{@model.previewCounts.unchanged}}
          @processed={{@model.counts.unchanged}}
          @previewCounts={{@model.previewCounts}}
        />
        <DetailsBox
          @title={{t "eligibility_files.new_employees"}}
          @category="created"
          @preview={{@model.previewCounts.created}}
          @processed={{@model.counts.created}}
          @previewCounts={{@model.previewCounts}}
          @canViewDetails={{this.canViewCreated}}
          @onViewDetails={{fn this.openViewDetails "created"}}
        />
        <DetailsBox
          @title={{t "eligibility_files.changed_employees"}}
          @category="updated"
          @preview={{@model.previewCounts.updated}}
          @processed={{@model.counts.updated}}
          @previewCounts={{@model.previewCounts}}
          @canViewDetails={{this.canViewUpdated}}
          @onViewDetails={{fn this.openViewDetails "updated"}}
          @canViewTopChanges={{this.canViewUpdated}}
          @onViewTopChanges={{this.openTopChanges}}
        />
        <DetailsBox
          @title={{t "eligibility_files.deactivated_employees"}}
          @category="deactivated"
          @preview={{@model.previewCounts.deactivated}}
          @processed={{@model.counts.deactivated}}
          @previewCounts={{@model.previewCounts}}
          @canViewDetails={{this.canViewDeactivated}}
          @onViewDetails={{fn this.openViewDetails "deactivated"}}
        />
        <DetailsBox
          @title={{t "eligibility_files.reactivated_employees"}}
          @category="reactivated"
          @preview={{@model.previewCounts.reactivated}}
          @processed={{@model.counts.reactivated}}
          @previewCounts={{@model.previewCounts}}
          @canViewDetails={{this.canViewReactivated}}
          @onViewDetails={{fn this.openViewDetails "reactivated"}}
        />
      </div>

      <div class="tio-flex-loose">
        <Button
          @intent="primary"
          {{on "click" this.processEligibilityFile}}
          @isDisabled={{or (not this.processEnabled) this.isProcessing}}
        >
          {{t "eligibility_files.process_eligibility_file"}}
        </Button>
        <Button
          @intent="primary"
          {{on "click" this.abortEligibilityFile}}
          @isDisabled={{or (not this.abortEnabled) this.isAborting}}
        >
          {{t "eligibility_files.abort"}}
        </Button>
        <EligibilityFilesDownloadEligibilityFile
          @processId={{@model.processId}}
          {{! @glint-expect-error: not sure}}
          @companyId={{@model.company.id}}
          @legacyId={{@model.company.legacyId}}
          @fileName={{@model.fileName}}
          @format="text-button"
          @title=""
        />
      </div>
      <Section>
        <:header>{{t "eligibility_files.process_details"}}</:header>
        <:body>
          <div class="grid grid-cols-4">
            <div class="col-span-2">
              <h2 class="text-sm inline-block">
                <div>
                  {{t "eligibility_files.status"}}
                  <span class="tio-fg-brand font-bold">
                    {{t (concat "common.eligibility_file_upload.status." @model.status)}}
                  </span>
                  {{#if @model.isUploading}}
                    <div class="mr-12 mt-4">
                      <PhInfo
                        @size="24"
                        @weight="fill"
                        class="font-semibold align-bottom cursor-default tio-fg-disabled"
                      />
                      <span class="tio-text-sm-semibold align-bottom cursor-default tio-fg-info">
                        {{t "eligibility_files.upload_warning"}}
                      </span>
                    </div>
                  {{/if}}
                  <br />
                  {{t "eligibility_files.total_processed"}}
                  <span class="tio-fg-brand font-bold">{{@model.totalProcessed}}
                  </span>
                  {{#if @model.isUploading}}
                    <TioLoadingSpinner />
                  {{/if}}
                </div>
              </h2>
            </div>
            <div class="col-span-4 grid grid-cols-2">
              <span class="col-span-1">{{t "eligibility_files.uploaded_by"}}</span>
              <span class="col-span-1 float-right font-semibold">{{this.uploaderName}}</span>
              <span class="col-span-1">{{t "eligibility_files.file_name"}}</span>
              <span class="col-span-1 float-right font-semibold">
                {{@model.fileName}}
              </span>
              <span class="col-span-1">{{t "eligibility_files.upload_type"}}</span>
              <span class="col-span-1 float-right font-semibold">
                {{#if (eq @model.strategy "MANUAL")}}
                  {{t "eligibility_files.manual"}}
                {{else if (eq @model.strategy "MANUAL_FORCE_UPDATE")}}
                  {{t "eligibility_files.forced"}}
                {{else}}
                  {{t "eligibility_files.automatic"}}
                {{/if}}
              </span>
              <span class="col-span-1">{{t "eligibility_files.start_time"}}</span>
              <span class="col-span-1 float-right font-semibold">
                {{safeFormatDate @model.startTime}}
                {{safeFormatTime @model.startTime}}
              </span>
              {{#if @model.abortedAt}}
                <span class="col-span-1">{{t "eligibility_files.time_aborted"}}</span>
                <span class="col-span-1 float-right font-semibold">{{@model.abortedAt}}</span>
              {{/if}}
              {{#if @model.endTime}}
                <span class="col-span-1">{{t "eligibility_files.time_completed"}}</span>
                <span class="col-span-1 float-right font-semibold">
                  {{safeFormatDate @model.endTime}}
                  {{safeFormatTime @model.endTime}}
                </span>
              {{/if}}
            </div>
          </div>
        </:body>
      </Section>
      <Section>
        <:header>{{t "eligibility_files.warning.default"}}</:header>
        <:body>
          <Table>
            <:thead as |options|>
              <options.tr>
                <options.th>{{t "eligibility_files.warning.type"}}</options.th>
                <options.th>{{t "eligibility_files.warning.log_level"}}</options.th>
                <options.th>{{t "eligibility_files.warning.description"}}</options.th>
              </options.tr>
            </:thead>
            <:tbody as |options|>
              {{#each @model.eligibilityFileJobLogEntries as |entry|}}
                <options.tr>
                  <options.td>{{entry.key}}</options.td>
                  <options.td>{{entry.logLevel}}</options.td>
                  <options.td>{{entry.message}}</options.td>
                </options.tr>
              {{/each}}
            </:tbody>
          </Table>
        </:body>
      </Section>

      {{#if this.activeCategory}}
        <RecordDetailsModal
          @isOpen={{this.isRecordModalOpen}}
          @onClose={{this.closeRecordModal}}
          @jobLogId={{@model.id}}
          @category={{this.activeCategory}}
          @title={{this.activeCategoryTitle}}
        />
      {{/if}}

      <TopChangesModal
        @isOpen={{this.isTopChangesOpen}}
        @onClose={{this.closeTopChanges}}
        @jobLogId={{@model.id}}
      />
    </VStack>
  </template>
}
